import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  apiService,
  PlayerStatsResponse,
  StatsDataPoint,
  ServerStatsDataPoint,
} from '@/services/api.service';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import styles from './PlayerStatsPage.module.scss';

// Duration options with labels
const DURATION_OPTIONS = [
  { value: 10, label: '10 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1h' },
  { value: 180, label: '3h' },
  { value: 1440, label: '24h' },
  { value: 4320, label: '3j' },
  { value: 10080, label: '7j' },
  { value: 20160, label: '14j' },
  { value: 43200, label: '30j' },
  { value: 86400, label: '60j' },
  { value: 129600, label: '90j' },
  { value: 262800, label: '6 mois' },
  { value: 525600, label: '1 an' },
];

// Server groups to merge (lowercase for matching)
const SERVER_GROUPS: Record<string, string[]> = {
  'Mining': ['mining'],
  'Farmland': ['farmland'],
  'Lobby': ['lobby'],
};

// Colors for different series
const COLORS = {
  total: '#3B82F6', // Blue
  staff: '#F59E0B', // Orange
  servers: [
    '#10B981', // Green
    '#8B5CF6', // Purple
    '#EF4444', // Red
    '#EC4899', // Pink
    '#14B8A6', // Teal
    '#F97316', // Orange
    '#6366F1', // Indigo
    '#84CC16', // Lime
  ],
};

// Chart info type for fullscreen and comparison
interface ChartInfo {
  id: string;
  name: string;
  color: string;
  data: ServerStatsDataPoint[];
  type: 'global' | 'server';
  dataKey?: 'totalPlayers' | 'staffCount';
  globalData?: StatsDataPoint[];
}

export function PlayerStatsPage() {
  const navigate = useNavigate();
  const [duration, setDuration] = useState(60); // Default 1h
  const [stats, setStats] = useState<PlayerStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number;
    y: number;
    data: StatsDataPoint | ServerStatsDataPoint;
    label: string;
    isAggregated: boolean;
  } | null>(null);

  // Fullscreen modal state
  const [fullscreenChart, setFullscreenChart] = useState<ChartInfo | null>(null);

  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedCharts, setSelectedCharts] = useState<string[]>([]);

  // Determine if data is aggregated (duration > 3h)
  const isAggregated = duration > 180;

  useEffect(() => {
    loadStats();
  }, [duration]);

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    const result = await apiService.getPlayerStats(duration);

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setStats(result.data);
    }

    setLoading(false);
  };

  // Process server stats: merge groups (mining, farmland, lobby) and keep others separate
  const processedServerStats = useMemo(() => {
    if (!stats) return { merged: {} as Record<string, ServerStatsDataPoint[]>, individual: {} as Record<string, ServerStatsDataPoint[]> };

    const merged: Record<string, ServerStatsDataPoint[]> = {};
    const individual: Record<string, ServerStatsDataPoint[]> = {};

    // First, identify which servers belong to which group
    const serverToGroup: Record<string, string> = {};
    const groupedServers: Record<string, string[]> = {};

    Object.keys(stats.serverStats).forEach((serverName) => {
      const lowerName = serverName.toLowerCase();
      let foundGroup: string | null = null;

      for (const [groupName, patterns] of Object.entries(SERVER_GROUPS)) {
        if (patterns.some((pattern) => lowerName.includes(pattern))) {
          foundGroup = groupName;
          break;
        }
      }

      if (foundGroup) {
        serverToGroup[serverName] = foundGroup;
        if (!groupedServers[foundGroup]) {
          groupedServers[foundGroup] = [];
        }
        groupedServers[foundGroup]!.push(serverName);
      } else {
        // Not in any group, keep individual
        individual[serverName] = stats.serverStats[serverName] ?? [];
      }
    });

    // Merge grouped servers
    for (const [groupName, servers] of Object.entries(groupedServers)) {
      if (servers.length === 0) continue;

      // Get all timestamps from all servers in the group
      const allTimestamps = new Set<number>();
      servers.forEach((serverName) => {
        stats.serverStats[serverName]?.forEach((point) => {
          allTimestamps.add(point.timestamp);
        });
      });

      const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

      // Create merged data points
      merged[groupName] = sortedTimestamps.map((timestamp) => {
        let totalAvg = 0;
        let totalMin = 0;
        let totalMax = 0;

        servers.forEach((serverName) => {
          const point = stats.serverStats[serverName]?.find((p) => p.timestamp === timestamp);
          if (point) {
            totalAvg += point.avg;
            totalMin += point.min;
            totalMax += point.max;
          }
        });

        return {
          timestamp,
          avg: totalAvg,
          min: totalMin,
          max: totalMax,
        };
      });
    }

    return { merged, individual };
  }, [stats]);

  // Build list of all charts for comparison mode
  const allCharts = useMemo((): ChartInfo[] => {
    if (!stats) return [];

    const charts: ChartInfo[] = [];
    let colorIndex = 0;

    // Global charts
    charts.push({
      id: 'total',
      name: 'Joueurs Total',
      color: COLORS.total,
      data: stats.globalStats.map((d) => ({ timestamp: d.timestamp, avg: d.totalPlayersAvg, min: d.totalPlayersMin, max: d.totalPlayersMax })),
      type: 'global',
      dataKey: 'totalPlayers',
      globalData: stats.globalStats,
    });

    charts.push({
      id: 'staff',
      name: 'Staff en ligne',
      color: COLORS.staff,
      data: stats.globalStats.map((d) => ({ timestamp: d.timestamp, avg: d.staffCountAvg, min: d.staffCountMin, max: d.staffCountMax })),
      type: 'global',
      dataKey: 'staffCount',
      globalData: stats.globalStats,
    });

    // Merged server charts
    Object.entries(processedServerStats.merged).forEach(([name, data]) => {
      charts.push({
        id: `merged-${name}`,
        name,
        color: COLORS.servers[colorIndex % COLORS.servers.length] ?? '#888',
        data,
        type: 'server',
      });
      colorIndex++;
    });

    // Individual server charts
    Object.entries(processedServerStats.individual).forEach(([name, data]) => {
      charts.push({
        id: `server-${name}`,
        name,
        color: COLORS.servers[colorIndex % COLORS.servers.length] ?? '#888',
        data,
        type: 'server',
      });
      colorIndex++;
    });

    return charts;
  }, [stats, processedServerStats]);

  // Format timestamp based on duration
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    if (duration <= 180) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (duration <= 1440) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (duration <= 10080) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    }
  };

  const toggleChartSelection = (chartId: string) => {
    setSelectedCharts((prev) =>
      prev.includes(chartId) ? prev.filter((id) => id !== chartId) : [...prev, chartId]
    );
  };

  const selectedChartsData = useMemo(() => {
    return allCharts.filter((chart) => selectedCharts.includes(chart.id));
  }, [allCharts, selectedCharts]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button className={styles.backButton} onClick={() => navigate('/stats')}>
            ← Statistiques
          </button>
          <h1 className={styles.title}>Statistiques Joueurs <GameGroupBadge size="md" /></h1>
          <p className={styles.subtitle}>
            Historique du nombre de joueurs connectés
          </p>
        </div>

        {/* Duration selector */}
        <div className={styles.durationSelector}>
          {DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`${styles.durationButton} ${duration === opt.value ? styles.active : ''}`}
              onClick={() => setDuration(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      {/* Comparison mode toggle */}
      <div className={styles.toolbar}>
        <button
          className={`${styles.toolbarButton} ${comparisonMode ? styles.active : ''}`}
          onClick={() => {
            setComparisonMode(!comparisonMode);
            if (comparisonMode) setSelectedCharts([]);
          }}
        >
          {comparisonMode ? '✕ Quitter comparaison' : '📊 Comparer des graphiques'}
        </button>

        {comparisonMode && selectedCharts.length >= 2 && (
          <button
            className={styles.toolbarButtonPrimary}
            onClick={() => setFullscreenChart({ id: 'comparison', name: 'Comparaison', color: '', data: [], type: 'server' })}
          >
            Voir la comparaison ({selectedCharts.length} sélectionnés)
          </button>
        )}
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Chargement des statistiques...</span>
        </div>
      ) : !stats || stats.globalStats.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📊</span>
          <p>Aucune donnée disponible pour cette période</p>
          <span className={styles.emptyHint}>
            Les snapshots sont créés toutes les minutes. Attendez quelques minutes pour voir les premières données.
          </span>
        </div>
      ) : (
        <div className={styles.chartsContainer}>
          {/* Render all charts */}
          {allCharts.map((chart) => (
            <div
              key={chart.id}
              className={`${styles.chartCard} ${comparisonMode ? styles.selectable : ''} ${selectedCharts.includes(chart.id) ? styles.selected : ''}`}
              onClick={comparisonMode ? () => toggleChartSelection(chart.id) : undefined}
            >
              <div className={styles.chartHeader}>
                <h2 className={styles.chartTitle}>
                  {comparisonMode && (
                    <span className={`${styles.checkbox} ${selectedCharts.includes(chart.id) ? styles.checked : ''}`}>
                      {selectedCharts.includes(chart.id) ? '✓' : ''}
                    </span>
                  )}
                  <span className={styles.chartDot} style={{ backgroundColor: chart.color }} />
                  {chart.name}
                </h2>
                <div className={styles.chartActions}>
                  <span className={styles.chartValue}>
                    {chart.data[chart.data.length - 1]?.avg ?? 0} actuellement
                  </span>
                  {!comparisonMode && (
                    <button
                      className={styles.fullscreenButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFullscreenChart(chart);
                      }}
                      title="Plein écran"
                    >
                      ⛶
                    </button>
                  )}
                </div>
              </div>
              <ServerLineChart
                data={chart.data}
                color={chart.color}
                isAggregated={isAggregated}
                formatTime={formatTime}
                onHover={(point, x, y) =>
                  !comparisonMode && setHoveredPoint(point ? { x, y, data: point, label: chart.name, isAggregated } : null)
                }
              />
            </div>
          ))}
        </div>
      )}

      {/* Tooltip */}
      {hoveredPoint && !comparisonMode && (
        <div
          className={styles.tooltip}
          style={{
            left: hoveredPoint.x + 10,
            top: hoveredPoint.y - 10,
          }}
        >
          <div className={styles.tooltipLabel}>{hoveredPoint.label}</div>
          <div className={styles.tooltipTime}>
            {formatTime((hoveredPoint.data as ServerStatsDataPoint).timestamp)}
          </div>
          {hoveredPoint.isAggregated ? (
            <>
              <div className={styles.tooltipRow}>
                <span>Moyenne:</span>
                <strong>{(hoveredPoint.data as ServerStatsDataPoint).avg}</strong>
              </div>
              <div className={styles.tooltipRow}>
                <span>Min:</span>
                <strong>{(hoveredPoint.data as ServerStatsDataPoint).min}</strong>
              </div>
              <div className={styles.tooltipRow}>
                <span>Max:</span>
                <strong>{(hoveredPoint.data as ServerStatsDataPoint).max}</strong>
              </div>
            </>
          ) : (
            <div className={styles.tooltipRow}>
              <span>Valeur:</span>
              <strong>{(hoveredPoint.data as ServerStatsDataPoint).avg}</strong>
            </div>
          )}
        </div>
      )}

      {/* Fullscreen Modal */}
      {fullscreenChart && (
        <div className={styles.fullscreenOverlay} onClick={() => setFullscreenChart(null)}>
          <div className={styles.fullscreenModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.fullscreenHeader}>
              <h2>
                {fullscreenChart.id === 'comparison' ? (
                  <>Comparaison</>
                ) : (
                  <>
                    <span className={styles.chartDot} style={{ backgroundColor: fullscreenChart.color }} />
                    {fullscreenChart.name}
                  </>
                )}
              </h2>
              <button className={styles.closeButton} onClick={() => setFullscreenChart(null)}>
                ✕
              </button>
            </div>
            <div className={styles.fullscreenContent}>
              {fullscreenChart.id === 'comparison' ? (
                <MultiLineChart
                  charts={selectedChartsData}
                  isAggregated={isAggregated}
                  formatTime={formatTime}
                />
              ) : (
                <FullscreenLineChart
                  data={fullscreenChart.data}
                  color={fullscreenChart.color}
                  isAggregated={isAggregated}
                  formatTime={formatTime}
                  name={fullscreenChart.name}
                />
              )}
            </div>

            {/* Legend for comparison mode */}
            {fullscreenChart.id === 'comparison' && (
              <div className={styles.comparisonLegend}>
                {selectedChartsData.map((chart) => (
                  <div key={chart.id} className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ backgroundColor: chart.color }} />
                    <span>{chart.name}</span>
                    <span className={styles.legendValue}>{chart.data[chart.data.length - 1]?.avg ?? 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Server Line Chart Component
interface ServerLineChartProps {
  data: ServerStatsDataPoint[];
  color: string;
  isAggregated: boolean;
  formatTime: (ts: number) => string;
  onHover: (point: ServerStatsDataPoint | null, x: number, y: number) => void;
}

export function ServerLineChart({ data, color, isAggregated, formatTime, onHover }: ServerLineChartProps) {
  const width = 800;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const minValues = data.map((d) => d.min);
  const maxValues = data.map((d) => d.max);

  const minVal = Math.min(...minValues, 0);
  const maxVal = Math.max(...maxValues, 1);
  const range = maxVal - minVal || 1;

  const xScale = (index: number) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
  const yScale = (value: number) => padding.top + chartHeight - ((value - minVal) / range) * chartHeight;

  const linePath = data.map((d, i) => {
    const x = xScale(i);
    const y = yScale(d.avg);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  let areaPath = '';
  if (isAggregated && data.length > 1) {
    const topPath = data.map((d, i) => {
      const x = xScale(i);
      const y = yScale(d.max);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const bottomPath = [...data].reverse().map((d, i) => {
      const x = xScale(data.length - 1 - i);
      const y = yScale(d.min);
      return `L ${x} ${y}`;
    }).join(' ');

    areaPath = `${topPath} ${bottomPath} Z`;
  }

  const yTicks = Array.from({ length: 5 }, (_, i) => minVal + (range * i) / 4);

  const xLabelCount = Math.min(6, data.length);
  const xLabels = Array.from({ length: xLabelCount }, (_, i) => {
    const index = Math.floor((i / (xLabelCount - 1 || 1)) * (data.length - 1));
    return { index, timestamp: data[index]?.timestamp };
  });

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    // Position souris -> unités viewBox (gère le letterbox de preserveAspectRatio="meet").
    const ctm = e.currentTarget.getScreenCTM();
    if (!ctm) return;
    const relX = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse()).x - padding.left;

    if (relX >= 0 && relX <= chartWidth && data.length > 0) {
      const index = Math.round((relX / chartWidth) * (data.length - 1));
      const validIndex = Math.max(0, Math.min(index, data.length - 1));
      onHover(data[validIndex] ?? null, e.clientX, e.clientY);
    }
  };

  const handleMouseLeave = () => {
    onHover(null, 0, 0);
  };

  if (data.length === 0) {
    return (
      <div className={styles.noData}>
        <span>Aucune donnée</span>
      </div>
    );
  }

  return (
    <svg
      className={styles.chart}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {yTicks.map((tick, i) => (
        <line
          key={i}
          x1={padding.left}
          y1={yScale(tick)}
          x2={width - padding.right}
          y2={yScale(tick)}
          className={styles.gridLine}
        />
      ))}

      {isAggregated && areaPath && (
        <path d={areaPath} fill={color} fillOpacity={0.1} />
      )}

      {data.length > 1 && (
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} />
      )}

      {data.map((d, i) => (
        <circle
          key={i}
          cx={xScale(i)}
          cy={yScale(d.avg)}
          r={3}
          fill={color}
          className={styles.dataPoint}
        />
      ))}

      {yTicks.map((tick, i) => (
        <text
          key={i}
          x={padding.left - 8}
          y={yScale(tick) + 4}
          className={styles.axisLabel}
          textAnchor="end"
        >
          {Math.round(tick)}
        </text>
      ))}

      {xLabels.map(({ index, timestamp }) => (
        <text
          key={index}
          x={xScale(index)}
          y={height - 8}
          className={styles.axisLabel}
          textAnchor="middle"
        >
          {timestamp !== undefined ? formatTime(timestamp) : ''}
        </text>
      ))}
    </svg>
  );
}

// Fullscreen Line Chart Component
interface FullscreenLineChartProps {
  data: ServerStatsDataPoint[];
  color: string;
  isAggregated: boolean;
  formatTime: (ts: number) => string;
  name: string;
}

function FullscreenLineChart({ data, color, isAggregated, formatTime, name }: FullscreenLineChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const width = 1200;
  const height = 500;
  const padding = { top: 30, right: 30, bottom: 50, left: 70 };

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const minVal = Math.min(...data.map((d) => d.min), 0);
  const maxVal = Math.max(...data.map((d) => d.max), 1);
  const range = maxVal - minVal || 1;

  const xScale = (index: number) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
  const yScale = (value: number) => padding.top + chartHeight - ((value - minVal) / range) * chartHeight;

  const linePath = data.map((d, i) => {
    const x = xScale(i);
    const y = yScale(d.avg);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  let areaPath = '';
  if (isAggregated && data.length > 1) {
    const topPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.max)}`).join(' ');
    const bottomPath = [...data].reverse().map((d, i) => `L ${xScale(data.length - 1 - i)} ${yScale(d.min)}`).join(' ');
    areaPath = `${topPath} ${bottomPath} Z`;
  }

  const yTicks = Array.from({ length: 8 }, (_, i) => minVal + (range * i) / 7);
  const xLabelCount = Math.min(10, data.length);
  const xLabels = Array.from({ length: xLabelCount }, (_, i) => {
    const index = Math.floor((i / (xLabelCount - 1 || 1)) * (data.length - 1));
    return { index, timestamp: data[index]?.timestamp };
  });

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const ctm = e.currentTarget.getScreenCTM();
    if (!ctm) return;
    const relX = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse()).x - padding.left;

    setMousePos({ x: e.clientX, y: e.clientY });

    if (relX >= 0 && relX <= chartWidth && data.length > 0) {
      const index = Math.round((relX / chartWidth) * (data.length - 1));
      setHoveredIndex(Math.max(0, Math.min(index, data.length - 1)));
    }
  };

  return (
    <div className={styles.fullscreenChartWrapper}>
      <svg
        className={styles.fullscreenChart}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {yTicks.map((tick, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={yScale(tick)}
            x2={width - padding.right}
            y2={yScale(tick)}
            className={styles.gridLine}
          />
        ))}

        {isAggregated && areaPath && (
          <path d={areaPath} fill={color} fillOpacity={0.15} />
        )}

        {data.length > 1 && (
          <path d={linePath} fill="none" stroke={color} strokeWidth={3} />
        )}

        {data.map((d, i) => (
          <circle
            key={i}
            cx={xScale(i)}
            cy={yScale(d.avg)}
            r={hoveredIndex === i ? 6 : 4}
            fill={color}
            className={styles.dataPoint}
          />
        ))}

        {yTicks.map((tick, i) => (
          <text key={i} x={padding.left - 12} y={yScale(tick) + 5} className={styles.axisLabelLarge} textAnchor="end">
            {Math.round(tick)}
          </text>
        ))}

        {xLabels.map(({ index, timestamp }) => (
          <text key={index} x={xScale(index)} y={height - 15} className={styles.axisLabelLarge} textAnchor="middle">
            {timestamp !== undefined ? formatTime(timestamp) : ''}
          </text>
        ))}

        {/* Hover indicator */}
        {hoveredIndex !== null && data[hoveredIndex] && (
          <>
            <line
              x1={xScale(hoveredIndex)}
              y1={padding.top}
              x2={xScale(hoveredIndex)}
              y2={height - padding.bottom}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="4 4"
              opacity={0.5}
            />
            <circle cx={xScale(hoveredIndex)} cy={yScale(data[hoveredIndex].avg)} r={8} fill={color} stroke="white" strokeWidth={2} />
          </>
        )}
      </svg>

      {/* Fullscreen tooltip */}
      {hoveredIndex !== null && data[hoveredIndex] && (
        <div
          className={styles.fullscreenTooltip}
          style={{
            left: mousePos.x + 15,
            top: mousePos.y - 10,
          }}
        >
          <div className={styles.tooltipLabel}>{name}</div>
          <div className={styles.tooltipTime}>{formatTime(data[hoveredIndex].timestamp)}</div>
          {isAggregated ? (
            <>
              <div className={styles.tooltipRow}><span>Moyenne:</span><strong>{data[hoveredIndex].avg}</strong></div>
              <div className={styles.tooltipRow}><span>Min:</span><strong>{data[hoveredIndex].min}</strong></div>
              <div className={styles.tooltipRow}><span>Max:</span><strong>{data[hoveredIndex].max}</strong></div>
            </>
          ) : (
            <div className={styles.tooltipRow}><span>Valeur:</span><strong>{data[hoveredIndex].avg}</strong></div>
          )}
        </div>
      )}
    </div>
  );
}

// Multi-Line Chart Component for comparison
interface MultiLineChartProps {
  charts: ChartInfo[];
  isAggregated: boolean;
  formatTime: (ts: number) => string;
}

function MultiLineChart({ charts, formatTime }: MultiLineChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const width = 1200;
  const height = 500;
  const padding = { top: 30, right: 30, bottom: 50, left: 70 };

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find global min/max across all charts
  const allValues = charts.flatMap((chart) => chart.data.flatMap((d) => [d.avg, d.min, d.max]));
  const minVal = Math.min(...allValues, 0);
  const maxVal = Math.max(...allValues, 1);
  const range = maxVal - minVal || 1;

  // Find max data length for x-axis
  const maxDataLength = Math.max(...charts.map((c) => c.data.length));

  const xScale = (index: number, dataLength: number) => padding.left + (index / (dataLength - 1 || 1)) * chartWidth;
  const yScale = (value: number) => padding.top + chartHeight - ((value - minVal) / range) * chartHeight;

  const yTicks = Array.from({ length: 8 }, (_, i) => minVal + (range * i) / 7);

  // Use first chart's timestamps for x-axis labels
  const firstChartWithData = charts.find((c) => c.data.length > 0);
  const xLabelCount = Math.min(10, firstChartWithData?.data.length || 0);
  const xLabels = firstChartWithData
    ? Array.from({ length: xLabelCount }, (_, i) => {
        const index = Math.floor((i / (xLabelCount - 1 || 1)) * (firstChartWithData.data.length - 1));
        return { index, timestamp: firstChartWithData.data[index]?.timestamp };
      })
    : [];

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const ctm = e.currentTarget.getScreenCTM();
    if (!ctm) return;
    const relX = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse()).x - padding.left;

    setMousePos({ x: e.clientX, y: e.clientY });

    if (relX >= 0 && relX <= chartWidth && maxDataLength > 0) {
      const index = Math.round((relX / chartWidth) * (maxDataLength - 1));
      setHoveredIndex(Math.max(0, Math.min(index, maxDataLength - 1)));
    }
  };

  return (
    <div className={styles.fullscreenChartWrapper}>
      <svg
        className={styles.fullscreenChart}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {/* Grid lines */}
        {yTicks.map((tick, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={yScale(tick)}
            x2={width - padding.right}
            y2={yScale(tick)}
            className={styles.gridLine}
          />
        ))}

        {/* Draw each chart's line */}
        {charts.map((chart) => {
          const linePath = chart.data.map((d, i) => {
            const x = xScale(i, chart.data.length);
            const y = yScale(d.avg);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ');

          return (
            <g key={chart.id}>
              {chart.data.length > 1 && (
                <path d={linePath} fill="none" stroke={chart.color} strokeWidth={2.5} />
              )}
              {chart.data.map((d, i) => (
                <circle
                  key={i}
                  cx={xScale(i, chart.data.length)}
                  cy={yScale(d.avg)}
                  r={3}
                  fill={chart.color}
                />
              ))}
            </g>
          );
        })}

        {/* Y-axis labels */}
        {yTicks.map((tick, i) => (
          <text key={i} x={padding.left - 12} y={yScale(tick) + 5} className={styles.axisLabelLarge} textAnchor="end">
            {Math.round(tick)}
          </text>
        ))}

        {/* X-axis labels */}
        {xLabels.map(({ index, timestamp }) => (
          <text key={index} x={xScale(index, firstChartWithData?.data.length || 1)} y={height - 15} className={styles.axisLabelLarge} textAnchor="middle">
            {timestamp !== undefined ? formatTime(timestamp) : ''}
          </text>
        ))}

        {/* Hover indicator */}
        {hoveredIndex !== null && (
          <line
            x1={xScale(hoveredIndex, maxDataLength)}
            y1={padding.top}
            x2={xScale(hoveredIndex, maxDataLength)}
            y2={height - padding.bottom}
            stroke="rgba(255,255,255,0.3)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        )}
      </svg>

      {/* Multi-line tooltip */}
      {hoveredIndex !== null && (
        <div
          className={styles.fullscreenTooltip}
          style={{
            left: mousePos.x + 15,
            top: mousePos.y - 10,
          }}
        >
          <div className={styles.tooltipTime}>
            {firstChartWithData?.data[hoveredIndex] && formatTime(firstChartWithData.data[hoveredIndex].timestamp)}
          </div>
          {charts.map((chart) => {
            const point = chart.data[hoveredIndex];
            if (!point) return null;
            return (
              <div key={chart.id} className={styles.multiTooltipRow}>
                <span className={styles.legendDot} style={{ backgroundColor: chart.color }} />
                <span className={styles.multiTooltipName}>{chart.name}</span>
                <strong>{point.avg}</strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
